import { useEffect, useRef, useCallback, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { RealtimeChannel } from '@supabase/supabase-js';
import type { WorkflowState } from '@/types/workflow';
import {
  computeDelta, applyDelta, isDeltaEmpty,
  type WorkflowDelta,
} from '@/lib/workflowDelta';

/**
 * Per-peer presence payload broadcast on the channel. `email` is the
 * only required field; the rest let collaborators see each other's
 * focus without adding round-trips.
 */
export interface PresencePeer {
  email: string;
  online_at: string;
  /** Peer's user id — used for deterministic tie-breaking when electing
   *  a snapshot owner. Set automatically by `useRealtimeSync`. */
  userId?: string;
  /** Peer's assigned display colour (stable per userId). */
  color?: string;
  /** Last known cursor position in canvas coordinates. */
  cursor?: { x: number; y: number };
  /** The block/layer the peer currently has selected, if any. */
  selectedBlockId?: string | null;
}

/** Generate a stable hue from a user id so two peers don't collide. */
function colorFromUserId(id: string): string {
  let hash = 0;
  for (let i = 0; i < id.length; i++) hash = (hash * 31 + id.charCodeAt(i)) & 0xffffffff;
  const hue = Math.abs(hash) % 360;
  return `hsl(${hue} 70% 50%)`;
}

interface RealtimeSyncOptions {
  /** Supabase workflow document ID (null = not syncing) */
  documentId: string | null;
  /** Current user ID */
  userId: string | null;
  /** Get the current workflow state */
  getState: () => WorkflowState;
  /** Apply an incoming remote state */
  loadState: (state: WorkflowState) => void;
  /** Optional: current cursor position to broadcast as presence. */
  cursor?: { x: number; y: number } | null;
  /** Optional: currently-selected block id to broadcast as presence. */
  selectedBlockId?: string | null;
}

interface RealtimeSyncReturn {
  /** Whether realtime sync is active */
  isSyncing: boolean;
  /** Number of connected collaborators (including self) */
  collaboratorCount: number;
  /** List of connected user emails (legacy, back-compat). */
  collaborators: string[];
  /** Full presence payloads for rendering cursors / avatars / selections. */
  peers: PresencePeer[];
  /** Push current state to the channel */
  pushUpdate: () => void;
}

/**
 * Hook for real-time workflow synchronization via Supabase Realtime.
 *
 * When a documentId and userId are provided, this hook:
 * 1. Joins a Realtime channel scoped to that document
 * 2. Tracks presence (who is editing)
 * 3. Broadcasts workflow state changes to all collaborators
 * 4. Listens for remote changes and applies them
 */
export function useRealtimeSync({
  documentId,
  userId,
  getState,
  loadState,
  cursor = null,
  selectedBlockId = null,
}: RealtimeSyncOptions): RealtimeSyncReturn {
  const channelRef = useRef<RealtimeChannel | null>(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const [collaborators, setCollaborators] = useState<string[]>([]);
  const [peers, setPeers] = useState<PresencePeer[]>([]);
  const peersRef = useRef<PresencePeer[]>([]);
  const lastPushRef = useRef<number>(0);
  // Adaptive throttle. Defaults to 500 ms but widens up to 1500 ms when
  // channel echoes are crowding the receive path (many peers / high
  // broadcast rate) and tightens down to 200 ms when alone on the
  // channel — a solo editor has no one to collide with.
  const throttleRef = useRef<number>(500);
  const lastIncomingRef = useRef<number>(0);
  // Refs for presence values we want current-but-not-reactive: re-tracking
  // presence on every cursor move would spam the channel. Callers can call
  // the returned `pushUpdate` cadence; separately we update track() on a
  // throttled basis below.
  const cursorRef = useRef(cursor);
  cursorRef.current = cursor;
  const selectedBlockIdRef = useRef(selectedBlockId);
  selectedBlockIdRef.current = selectedBlockId;
  const lastBroadcastStateRef = useRef<WorkflowState | null>(null);
  const getStateRef = useRef(getState);
  getStateRef.current = getState;
  const loadStateRef = useRef(loadState);
  loadStateRef.current = loadState;

  // Broadcast a DELTA (not the full snapshot) of everything that changed since
  // the previous broadcast. Receivers merge the delta into THEIR current
  // state so in-progress local edits to other blocks survive peer updates.
  const pushUpdate = useCallback(() => {
    const channel = channelRef.current;
    if (!channel || !userId) return;

    // Adaptive throttle: widen when the channel is hot (recent inbound
     // broadcasts), tighten when we appear to be alone.
    const now = Date.now();
    const peerCount = peersRef.current.length;
    const recentIncoming = now - lastIncomingRef.current < 2000;
    if (peerCount <= 1) {
      throttleRef.current = 200;
    } else if (recentIncoming && peerCount >= 3) {
      throttleRef.current = 1500;
    } else {
      throttleRef.current = 500;
    }
    if (now - lastPushRef.current < throttleRef.current) return;

    const state = getStateRef.current();
    const prev = lastBroadcastStateRef.current;
    if (!prev) {
      // First push in this session. To avoid two peers that joined
      // simultaneously clobbering each other's state, the peer that's
      // been online longest owns the initial snapshot. Ties break on
      // lex-smallest userId so the decision is deterministic across
      // clients without coordination.
      const ownSelfEntry = peersRef.current.find(p => p.userId === userId);
      const ownOnline = ownSelfEntry?.online_at ?? new Date(now).toISOString();
      const isSnapshotOwner = !peersRef.current.some(p => {
        if (p.userId === userId) return false;
        const cmp = (p.online_at ?? '').localeCompare(ownOnline);
        if (cmp < 0) return true; // peer joined earlier
        if (cmp > 0) return false;
        // Tiebreak on userId lex order (smallest wins)
        return (p.userId ?? '').localeCompare(userId) < 0;
      });
      if (!isSnapshotOwner) {
        // Wait for the owner's snapshot; `lastBroadcastStateRef` will
        // be seeded by the workflow-snapshot listener below.
        lastPushRef.current = now;
        return;
      }
      lastPushRef.current = now;
      lastBroadcastStateRef.current = state;
      channel.send({
        type: 'broadcast',
        event: 'workflow-snapshot',
        payload: { state, userId, timestamp: now },
      });
      return;
    }
    const delta = computeDelta(prev, state);
    if (isDeltaEmpty(delta)) return;
    lastPushRef.current = now;
    lastBroadcastStateRef.current = state;
    channel.send({
      type: 'broadcast',
      event: 'workflow-delta',
      payload: { delta, userId, timestamp: now },
    });
  }, [userId]);

  useEffect(() => {
    if (!documentId || !userId) {
      setIsSyncing(false);
      setCollaborators([]);
      return;
    }

    const channelName = `workflow:${documentId}`;
    const channel = supabase.channel(channelName, {
      config: { presence: { key: userId } },
    });

    // Track presence — pull full payload so the TopBar badge + future
    // cursor/avatar overlays can render without extra round-trips.
    channel.on('presence', { event: 'sync' }, () => {
      const presenceState = channel.presenceState();
      const emails: string[] = [];
      const payloads: PresencePeer[] = [];
      for (const key in presenceState) {
        const entries = presenceState[key];
        if (entries && entries.length > 0) {
          const entry = entries[0] as unknown as PresencePeer;
          if (entry.email) emails.push(entry.email);
          payloads.push(entry);
        }
      }
      setCollaborators(emails);
      setPeers(payloads);
      peersRef.current = payloads;
    });

    // Legacy full-state event (kept for back-compat during the rollout).
    channel.on('broadcast', { event: 'workflow-update' }, ({ payload }) => {
      if (!payload || payload.userId === userId) return;
      lastIncomingRef.current = Date.now();
      try {
        loadStateRef.current(payload.state);
        lastBroadcastStateRef.current = payload.state;
      } catch { /* ignore malformed */ }
    });

    // Initial snapshot when a peer first joins — align our state to theirs.
    channel.on('broadcast', { event: 'workflow-snapshot' }, ({ payload }) => {
      if (!payload || payload.userId === userId) return;
      lastIncomingRef.current = Date.now();
      try {
        loadStateRef.current(payload.state);
        lastBroadcastStateRef.current = payload.state;
      } catch { /* ignore */ }
    });

    // Per-entity delta — merge into CURRENT local state so in-progress edits
    // to unrelated entities aren't clobbered.
    channel.on('broadcast', { event: 'workflow-delta' }, ({ payload }) => {
      if (!payload || payload.userId === userId) return;
      lastIncomingRef.current = Date.now();
      const delta = payload.delta as WorkflowDelta | undefined;
      if (!delta) return;
      try {
        const current = getStateRef.current();
        const merged = applyDelta(current, delta);
        loadStateRef.current(merged);
        lastBroadcastStateRef.current = merged;
      } catch { /* ignore */ }
    });

    // Reconnect-retry with exponential backoff. If the channel drops
    // (bad wifi, server restart), we resubscribe with 1s, 2s, 4s, ...
    // delays capped at 30s so the app doesn't go silent on the user.
    let reconnectAttempt = 0;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    const scheduleReconnect = () => {
      if (reconnectTimer) return;
      const delay = Math.min(30_000, 1000 * 2 ** reconnectAttempt);
      reconnectAttempt++;
      reconnectTimer = setTimeout(() => {
        reconnectTimer = null;
        if (channelRef.current === channel) channel.subscribe();
      }, delay);
    };

    channel
      .subscribe(async (status) => {
        if (status === 'SUBSCRIBED') {
          setIsSyncing(true);
          reconnectAttempt = 0; // reset backoff after a successful connect
          // Track self in presence with full payload
          const { data } = await supabase.auth.getUser();
          await channel.track({
            email: data.user?.email ?? 'Unknown',
            online_at: new Date().toISOString(),
            userId,
            color: colorFromUserId(userId),
            cursor: cursorRef.current ?? undefined,
            selectedBlockId: selectedBlockIdRef.current ?? undefined,
          } as PresencePeer);
        } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
          setIsSyncing(false);
          scheduleReconnect();
        }
      });

    channelRef.current = channel;

    return () => {
      if (reconnectTimer) { clearTimeout(reconnectTimer); reconnectTimer = null; }
      channel.unsubscribe();
      channelRef.current = null;
      lastBroadcastStateRef.current = null;
      setIsSyncing(false);
      setCollaborators([]);
      setPeers([]);
      peersRef.current = [];
    };
  }, [documentId, userId]);

  return {
    isSyncing,
    collaboratorCount: collaborators.length,
    collaborators,
    peers,
    pushUpdate,
  };
}
